import React, { useEffect, useRef, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  makeStyles,
} from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import { getRecordOfProceedings, startRecordOfProceedings } from "../service";

const useStyles = makeStyles((theme) => ({
  paperCard: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    // Same cap as the AI summary and Stage-wise Trail cards on the case page;
    // the list below the header scrolls inside the card.
    maxHeight: "90vh",
    boxSizing: "border-box",
    minWidth: 0,
    overflow: "hidden",
    padding: "2rem",
    border: "1px solid #E5E5E5",
    [theme.breakpoints.down("md")]: {
      padding: "2rem",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "1rem",
    },
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
    marginBottom: "1.2rem",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  metaRow: {
    flexShrink: 0,
    marginBottom: "1.5rem",
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: "0.5rem",
  },
  sentinel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "0.75rem 0",
  },
  heading: {
    fontWeight: "600",
    fontSize: "1.2rem",
    lineHeight: "1.5rem",
    color: "black",
  },
  placeholderText: {
    color: TEXT.muted,
    fontSize: "0.85rem",
  },
  errorText: {
    fontSize: "0.85rem",
    color: TEXT.error,
  },
  metaTitle: {
    fontSize: "0.8rem",
    color: TEXT.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "0.25rem",
  },
  metaValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: TEXT.primary,
  },
  docContainer: {
    padding: "0.8rem",
    backgroundColor: "#FAFAFA",
    border: "1px solid #F0F0F0",
    borderRadius: "4px",
    marginBottom: "0.5rem",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  docName: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    wordBreak: "break-all",
  },
  docMeta: {
    fontSize: "0.8rem",
    color: TEXT.muted,
    marginTop: "0.2rem",
  },
  docSummary: {
    fontSize: "0.875rem",
    lineHeight: 1.6,
    color: TEXT.secondary,
    marginTop: "0.5rem",
  },
}));

export default function RecordOfProceedings({ disputeId }) {
  const classes = useStyles();
  const [procData, setProcData] = useState(null);
  const [ropStatus, setRopStatus] = useState("loading");
  const [error, setError] = useState("");
  const ropTimer = useRef(null);
  const ropPollId = useRef(0);
  const isMounted = useRef(true);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  // Offset of the next page to request, and a re-entrancy guard for loadMore.
  const nextOffsetRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const READ_INTERVAL = 4000;
  const MAX_POLLS = 60;
  const PAGE_SIZE = 10;

  // Documents accumulated across pages — the API returns one page at a time.
  const [docs, setDocs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState("");
  const totalCount = procData?.document_count ?? docs.length;

  const stopPolling = () => {
    clearTimeout(ropTimer.current);
    ropTimer.current = null;
    ropPollId.current += 1;
  };

  const readRop = async ({ offset = 0, append = false } = {}) => {
    try {
      const { data } = await getRecordOfProceedings(disputeId, {
        offset,
        limit: PAGE_SIZE,
      });
      if (!isMounted.current) return data.status || "none";

      const page = data.documents || [];
      setProcData(data);
      setDocs((prev) => (append ? [...prev, ...page] : page));
      nextOffsetRef.current = data.next_offset ?? offset + page.length;
      setHasMore(
        typeof data.has_more === "boolean"
          ? data.has_more
          : offset + page.length < (data.document_count ?? 0)
      );

      const status = data.status || (page.length ? "done" : "none");
      setRopStatus(status);
      if (status === "failed") setError(data.status_message || "Generation failed.");
      else setError("");
      setLoadMoreError("");
      return status;
    } catch (err) {
      if (append) {
        // A failed page must not wipe out the pages already on screen.
        console.error("Error fetching more record of proceedings:", err);
        if (isMounted.current) setLoadMoreError("Could not load more items.");
        return "failed";
      }
      // 400/404 just means nothing has been generated for this case yet.
      const code = err?.response?.status;
      if (code === 400 || code === 404) {
        setError("");
        setRopStatus("none");
        return "none";
      }
      console.error("Error fetching record of proceedings:", err);
      setError("Failed to load Record of Proceedings.");
      setRopStatus("failed");
      return "failed";
    }
  };

  const pollRop = () => {
    stopPolling();
    const myId = ropPollId.current;
    let n = 0;
    const tick = async () => {
      n += 1;
      const s = await readRop();
      if (myId !== ropPollId.current || !isMounted.current) return;
      if (s !== "processing") return;
      if (n >= MAX_POLLS) {
        setRopStatus("timed_out");
        setError("Generation is taking longer than expected. Please try again.");
        return;
      }
      ropTimer.current = setTimeout(tick, READ_INTERVAL);
    };
    ropTimer.current = setTimeout(tick, READ_INTERVAL);
  };

  // Fetch the next page and append it to what is already on screen.
  const loadMore = async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    try {
      await readRop({ offset: nextOffsetRef.current, append: true });
    } finally {
      loadingMoreRef.current = false;
    }
  };

  const resetPaging = () => {
    nextOffsetRef.current = 0;
    loadingMoreRef.current = false;
    setDocs([]);
    setHasMore(false);
    setLoadMoreError("");
  };

  const regenerateRop = async () => {
    setError("");
    setRopStatus("processing");
    resetPaging();
    try {
      await startRecordOfProceedings(disputeId);
      pollRop();
    } catch (err) {
      console.error("Error starting record of proceedings:", err);
      setError("Could not start generation. Please try again.");
      setRopStatus("failed");
    }
  };

  useEffect(() => {
    if (!disputeId) return undefined;
    isMounted.current = true;
    resetPaging();
    setRopStatus("loading");
    (async () => {
      const s = await readRop();
      if (s === "processing") pollRop();
    })();
    return () => {
      isMounted.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disputeId]);

  // Infinite scroll: fetch the next page once the sentinel enters the scroll area.
  useEffect(() => {
    // While an error is showing, wait for the explicit Retry instead of looping.
    if (!hasMore || loadMoreError) return undefined;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: "120px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadMoreError, docs.length, ropStatus]);

  const renderContent = () => {
    if (ropStatus === "loading" || ropStatus === "processing") {
      return (
        <Box display="flex" alignItems="center" style={{ gap: "0.6rem" }}>
          <CircularProgress size={18} />
          <Typography className={classes.placeholderText}>
            {ropStatus === "processing"
              ? "Generation in progress…"
              : "Loading record of proceedings…"}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography className={classes.errorText}>
          {error}
        </Typography>
      );
    }

    if (ropStatus === "none") {
      return (
        <Typography className={classes.placeholderText}>
          No record of proceedings generated yet. Click Generate to create one.
        </Typography>
      );
    }

    if (!procData || docs.length === 0) {
      return (
        <Typography className={classes.placeholderText}>
          No structural proceedings available for this case.
        </Typography>
      );
    }

    return (
      <Box className={classes.contentWrapper}>
        {/* Case Metadata Headers */}
        <Grid container spacing={2} className={classes.metaRow}>
          {/* <Grid item xs={6}>
            <Typography className={classes.metaTitle}>Case Number</Typography>
            <Typography className={classes.metaValue}>
              {procData.case_number || "---"}
            </Typography>
          </Grid> */}
          <Grid item xs={6}>
            <Typography className={classes.metaTitle}>Total Action Items</Typography>
            <Typography className={classes.metaValue}>
              {totalCount} Items
            </Typography>
          </Grid>
        </Grid>

        {/* Document Action Entries — one page of 10 fetched at a time as the user scrolls */}
        <Box className={classes.scrollArea} ref={scrollRef}>
          {docs.map((doc, idx) => (
            <Box key={`${doc.name}-${idx}`} className={classes.docContainer}>
              <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
                <Grid item style={{ marginTop: "2px" }}>
                  <DescriptionIcon color="action" fontSize="small" />
                </Grid>
                <Grid item xs>
                  <Typography className={classes.docName}>
                    {doc.name}
                  </Typography>
                  <Typography className={classes.docMeta}>
                    Filed by <strong>{doc.filed_by}</strong> on {doc.date}
                  </Typography>
                  {doc.summary && (
                    <Typography className={classes.docSummary}>
                      {doc.summary}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}

          {hasMore ? (
            <Box className={classes.sentinel} ref={sentinelRef}>
              {loadMoreError ? (
                <>
                  <Typography className={classes.errorText}>
                    {loadMoreError}
                  </Typography>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => {
                      setLoadMoreError("");
                      loadMore();
                    }}
                  >
                    Retry
                  </Button>
                </>
              ) : (
                <>
                  <CircularProgress size={16} />
                  <Typography className={classes.placeholderText}>
                    Loading more…
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            docs.length > PAGE_SIZE && (
              <Box className={classes.sentinel}>
                <Typography className={classes.placeholderText}>
                  All {totalCount} items loaded
                </Typography>
              </Box>
            )
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Paper className={classes.paperCard}>
      <Box className={classes.headerRow}>
        <Typography className={classes.heading}>
          {procData?.disclaimer || "Record of Proceedings"}
        </Typography>
        {ropStatus !== "loading" && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={ropStatus === "processing"}
            onClick={regenerateRop}
          >
            {ropStatus === "none" ? "Generate" : "Regenerate"}
          </Button>
        )}
      </Box>
      {renderContent()}
    </Paper>
  );
}